import React from 'react';
import PropTypes from 'prop-types';
import PendingDataNotice from "./PendingDataNotice";
import CopyableContainer from "./CopyableContainer";
import LeagueTabHeading from "./LeagueTabHeading";
import LeagueTabSkeleton from "./LeagueTabSkeleton";
import SimulationPlayoffSummary, { getSimulationRowClassName } from "./SimulationPlayoffSummary";
import PlayoffTableImageCopyButton from "./PlayoffTableImageCopyButton";
import { playoffOddsImageOptions } from "../utils/playoffTableImage";
import { probabilityHeatStyle } from "../utils/simulationHeatmap";
import './styles/tableStyles.css';

const PlayoffOddsTable = ({
    data,
    playoffTeams,
    selectedWeek,
    pendingData,
    cached,
}) => {
    if (data === null || playoffTeams === null) {
        return <LeagueTabSkeleton variant="playoff-odds" label="Calculating playoff odds" />;
    }

    return (
        <CopyableContainer
            title={
                <div className="rank-distribution-heading">
                    <LeagueTabHeading title="Playoff Odds" />
                    <PlayoffTableImageCopyButton
                        imageOptions={playoffOddsImageOptions({
                            data,
                            playoffTeams,
                            selectedWeek,
                            cached,
                        })}
                        fileName={`playoff-odds-week-${selectedWeek}`}
                        disabled={!Array.isArray(data)}
                    />
                </div>
            }
            fileName={`playoff-odds-week-${selectedWeek}`}
            className="simulation-copyable-shell playoff-table-image-copyable playoff-odds-copyable"
        >
            <PendingDataNotice
                dataType="playoff odds"
                isPending={pendingData}
            />
            <div className="table-rounded-shell">
            <table className="table-with-bottom-caption">
                <thead>
                    <tr>
                        <th className="team-name-column">Team</th>
                        <th className="team-name-column">Owner</th>
                        <th>Projected<br />Wins</th>
                        <th>Projected<br />Losses</th>
                        <th>Projected<br />Ties</th>
                        <th>Projected<br />Points For</th>
                        <th>Playoff Odds</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((team, index) => (
                        <tr
                            key={index}
                            className={getSimulationRowClassName(index, playoffTeams, false)}
                        >
                            <td className="team-name-column">
                                {team.team}
                            </td>
                            <td className="team-name-column">
                                {team.owner}
                            </td>
                            <td>{team.projected_wins.toFixed(1)}</td>
                            <td>{team.projected_losses.toFixed(1)}</td>
                            <td>{team.projected_ties.toFixed(1)}</td>
                            <td>{team.projected_points_for.toFixed(2)}</td>
                            <td className="simulation-heat-cell">
                                <div
                                    className="simulation-heat"
                                    style={probabilityHeatStyle(team.playoff_odds)}
                                >
                                    {(team.playoff_odds * 100).toFixed(1)}%
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>
            <SimulationPlayoffSummary playoffTeams={playoffTeams} showByeChip={false} />
            <p className="simulation-table-disclaimer">
                    All playoff odds are based on simulation results alone.
                    Values of 0% or 100% do not necessarily mean that a team has
                    been mathematically eliminated or clinched a playoff spot.
            </p>
        </CopyableContainer>
    );
};

PlayoffOddsTable.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            team: PropTypes.string.isRequired,
            owner: PropTypes.string.isRequired,
            projected_wins: PropTypes.number.isRequired,
            projected_losses: PropTypes.number.isRequired,
            projected_ties: PropTypes.number.isRequired,
            projected_points_for: PropTypes.string.isRequired,
            playoff_odds: PropTypes.string.isRequired,
        })
    ),
    playoffTeams: PropTypes.number,
    cached: PropTypes.bool,
};

export default PlayoffOddsTable;
